import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import authService from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  currentUser: string | null;
  userRole: string | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function clearStoredSession() {
  localStorage.removeItem('auth');
  localStorage.removeItem('user');
  localStorage.removeItem('role');
  localStorage.removeItem('token');
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Verificar si ya está autenticado al cargar la página
  useEffect(() => {
    const checkAuth = async () => {
      const auth = localStorage.getItem('auth');
      const storedUser = localStorage.getItem('user');
      const storedRole = localStorage.getItem('role');
      const storedToken = localStorage.getItem('token');

      if (auth === 'true' && storedUser && storedToken) {
        // Verificar que la sesión sigue valiendo y el usuario sigue activo
        const result = await authService.verifySession();

        if (result.success) {
          setIsAuthenticated(true);
          setCurrentUser(storedUser);
          setUserRole(result.user?.role || storedRole);
        } else {
          // Sesión vencida, o usuario que ya no existe o fue desactivado
          clearStoredSession();
        }
      } else {
        // Sesión de antes del token, o incompleta: hay que volver a entrar
        clearStoredSession();
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Si la API rechaza el token (venció o es inválido), se cierra la sesión y
  // ProtectedRoute manda al login. El login propio no cuenta.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error?.config?.url || '';
        if (error?.response?.status === 401 && !url.startsWith('/api/auth/')) {
          clearStoredSession();
          setIsAuthenticated(false);
          setCurrentUser(null);
          setUserRole(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const result = await authService.login(username, password);

    if (result.success) {
      setIsAuthenticated(true);
      setCurrentUser(result.user || username);
      setUserRole(result.role || null);

      localStorage.setItem('auth', 'true');
      localStorage.setItem('user', result.user || username);
      localStorage.setItem('token', result.token || '');
      if (result.role) {
        localStorage.setItem('role', result.role);
      }

      return { success: true };
    }

    return { success: false, error: result.error };
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUserRole(null);

    clearStoredSession();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, currentUser, userRole, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
